import { EmailAttachment, EmailEnvelope, emptyEnvelope } from '../types/communication';

export const DEFAULT_LINK_EXPIRATION_MINUTES = 7 * 24 * 60;

export const attachmentTexts = (attachment: EmailAttachment): string[] => [attachment.source, attachment.fileName];

export const envelopeTexts = (envelope: EmailEnvelope): string[] => [
    envelope.subject,
    envelope.preheader,
    envelope.replyTo,
    ...envelope.from,
    ...envelope.to,
    ...envelope.cc,
    ...envelope.bcc,
    ...envelope.attachments.flatMap(attachmentTexts),
];

export const envelopeToJson = (envelope: EmailEnvelope): string =>
    JSON.stringify(
        {
            subject: envelope.subject,
            preheader: envelope.preheader || undefined,
            from: envelope.from[0] ?? '',
            to: envelope.to,
            cc: envelope.cc,
            bcc: envelope.bcc,
            replyTo: envelope.replyTo || undefined,
            attachments: envelope.attachments.map((a) => ({
                source: a.source,
                fileName: a.fileName || undefined,
                delivery: a.delivery,
                expirationMinutes: a.delivery === 'LINK' ? a.expirationMinutes ?? DEFAULT_LINK_EXPIRATION_MINUTES : undefined,
            })),
        },
        null,
        2,
    );

const asStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (typeof value === 'string' && value.length > 0) return [value];
    return [];
};

export const envelopeFromJson = (content: string | null | undefined): EmailEnvelope => {
    if (!content) return emptyEnvelope();
    let parsed: any;
    try {
        parsed = JSON.parse(content);
    } catch {
        return emptyEnvelope();
    }
    if (!parsed || typeof parsed !== 'object') return emptyEnvelope();
    const attachments: EmailAttachment[] = Array.isArray(parsed.attachments)
        ? parsed.attachments.map((a: any) => ({
              source: a?.source ?? '',
              fileName: a?.fileName ?? '',
              delivery: a?.delivery === 'LINK' ? 'LINK' : 'ATTACHMENT',
              expirationMinutes:
                  a?.delivery === 'LINK' ? a?.expirationMinutes ?? DEFAULT_LINK_EXPIRATION_MINUTES : undefined,
          }))
        : [];
    return {
        from: asStringArray(parsed.from),
        to: asStringArray(parsed.to),
        cc: asStringArray(parsed.cc),
        bcc: asStringArray(parsed.bcc),
        replyTo: typeof parsed.replyTo === 'string' ? parsed.replyTo : '',
        subject: typeof parsed.subject === 'string' ? parsed.subject : '',
        preheader: typeof parsed.preheader === 'string' ? parsed.preheader : '',
        attachments,
    };
};

export const expirationLabel = (minutes: number): string => {
    if (minutes % (24 * 60) === 0) return `${minutes / (24 * 60)} dia(s)`;
    if (minutes % 60 === 0) return `${minutes / 60} hora(s)`;
    return `${minutes} min`;
};
